// ENG-20861 in-house replacements for the quasar `debounce`/`throttle` utils.
// Semantics match quasar/src/utils/debounce.js and throttle.js exactly (incl.
// `debounce(...).cancel()`, relied on by several call sites).

export interface Debounced<F extends (...args: any[]) => any> {
  (this: any, ...args: Parameters<F>): void;
  cancel: () => void;
}

export function debounce<F extends (...args: any[]) => any>(
  fn: F,
  wait = 250,
  immediate?: boolean,
): Debounced<F> {
  let timer: ReturnType<typeof setTimeout> | null = null;

  const debounced = function (this: any, ...args: Parameters<F>) {
    const later = () => {
      timer = null;
      if (immediate !== true) {
        fn.apply(this, args);
      }
    };

    if (timer !== null) {
      clearTimeout(timer);
    } else if (immediate === true) {
      fn.apply(this, args);
    }

    timer = setTimeout(later, wait);
  } as Debounced<F>;

  debounced.cancel = () => {
    if (timer !== null) clearTimeout(timer);
  };

  return debounced;
}

export function throttle<F extends (...args: any[]) => any>(fn: F, limit = 250) {
  let waiting = false;
  let result: ReturnType<F>;

  return function (this: any, ...args: Parameters<F>): ReturnType<F> {
    if (!waiting) {
      waiting = true;
      setTimeout(() => {
        waiting = false;
      }, limit);
      result = fn.apply(this, args);
    }
    return result;
  };
}
